import { ArrowRight } from 'lucide-react';

import { TiltReveal } from '@/components/custom/tilt-reveal';

/**
 * The gradient-blob closing CTA card used across pages. `title` may contain
 * a trailing accent via `titleAccent` (rendered italic-peach).
 */
export function CtaBand({
  title,
  titleAccent,
  body,
  buttonLabel,
  buttonHref,
  aside,
}: {
  title: string;
  titleAccent?: string;
  body: string;
  buttonLabel: string;
  buttonHref: string;
  aside?: string;
}) {
  return (
    <section className="bg-background-alt px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24">
      <TiltReveal maxTilt={12} className="mx-auto max-w-[1248px]">
        <div className="relative flex flex-col items-center gap-10 overflow-hidden rounded-3xl border border-border bg-surface-pale px-6 py-12 md:px-10 md:py-16 lg:px-16 lg:py-[88px]">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                'radial-gradient(ellipse 70% 80% at 85% 15%, color-mix(in oklch, var(--primary) 35%, transparent) 0%, color-mix(in oklch, var(--primary) 14%, transparent) 35%, transparent 75%)',
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                'radial-gradient(ellipse 65% 75% at 10% 90%, color-mix(in oklch, var(--accent-soft) 70%, var(--primary)) 0%, color-mix(in oklch, var(--accent-soft) 38%, transparent) 35%, transparent 75%)',
            }}
          />
          <div className="relative z-10 flex flex-col items-center gap-5 text-center">
            <h2 className="font-display text-3xl md:text-5xl lg:text-[56px] lg:leading-[64px] font-bold text-foreground">
              {title}
              {titleAccent && (
                <>
                  {' '}
                  <span className="italic text-primary">{titleAccent}</span>
                </>
              )}
            </h2>
            <p className="max-w-[680px] text-[17px] leading-7 text-foreground-soft">{body}</p>
          </div>
          <div className="relative z-10 flex flex-col items-center gap-4 sm:flex-row">
            <a
              href={buttonHref}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3.5 text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {buttonLabel} <ArrowRight className="size-4" strokeWidth={2} />
            </a>
            {aside && <p className="font-mono text-xs tracking-[1.1px] text-muted-foreground">{aside}</p>}
          </div>
        </div>
      </TiltReveal>
    </section>
  );
}
